import type { SearchOutput, SearchResult } from "@orsane/schema/search";
import type { TaskListColor } from "@orsane/schema/task-lists";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { CommandEmpty, Command as CommandPrimitive } from "cmdk";
import { FileCheck, Inbox, List, Logs, PlusSquare, SearchIcon, Target, UserPen } from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDebounce } from "use-debounce";
import { create } from "zustand";
import { useNewTaskStore } from "@/common/stores";
import { cn } from "@/lib/utils";
import { searchQueryOptions } from "@/queries/queries";
import useNewListStore from "@/stores/useNewListStore";
import LoadingSpinner from "./loading-spinner";
import { TaskListBadgeColorShape } from "./TaskListBadge";
import { Command, CommandDialog, CommandGroup, CommandItem, CommandList } from "./ui/command";

type CommandMenuStore = {
    open: boolean;
    setOpen: (open: boolean) => void;
};

const useCommandMenuStore = create<CommandMenuStore>((set) => ({
    open: false,
    setOpen: (open: boolean) => set({ open }),
}));
export { useCommandMenuStore };

const commandIcons: Record<string, React.ReactElement | null> = {
    "add-list": <List />,
    "add-task": <PlusSquare />,
    task: <FileCheck />,
    tasklist: null,
    inbox: <Inbox />,
    "assigned-to-me": <Target />,
    "created-by-me": <UserPen />,
    all: <Logs />,
};

const commandGroupHeadings: Record<string, string> = {
    "add-list": "Actions",
    "add-task": "Actions",
    inbox: "Shortcuts",
    "assigned-to-me": "Shortcuts",
    "created-by-me": "Shortcuts",
    all: "Shortcuts",
    task: "Tasks",
    tasklist: "Task Lists",
};

export function CustomCommandInput({
    isLoading,
    className,
    ...props
}: React.ComponentProps<typeof CommandPrimitive.Input> & { isLoading: boolean }) {
    return (
        <div data-slot="command-input-wrapper" className="flex h-9 items-center gap-2 border-b px-3">
            {!isLoading && <SearchIcon className="size-4 shrink-0 opacity-50" />}
            {isLoading && <LoadingSpinner />}
            <CommandPrimitive.Input
                data-slot="command-input"
                className={cn(
                    "placeholder:text-muted-foreground flex h-10 w-full rounded-md bg-transparent py-3  outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
                    className
                )}
                {...props}
            />
        </div>
    );
}

export function CommandMenu() {
    const { open, setOpen } = useCommandMenuStore();
    const { setOpen: setNewListDialogOpen } = useNewListStore();

    const { setOpen: setNewTaskDialogOpen } = useNewTaskStore();
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery] = useDebounce(searchQuery, 200);
    const [selectedValue, setSelectedValue] = useState("");
    const { data, isFetching } = useQuery(searchQueryOptions({ open: true, query: debouncedSearchQuery }));
    const navigate = useNavigate();

    useHotkeys("mod+k", () => setOpen(true), { preventDefault: true });

    const items = useMemo(() => {
        let flatItems: SearchOutput[] = [
            { id: -1, type: "inbox", label: "Inbox", color: undefined },
            { id: -2, type: "assigned-to-me", label: "Assigned to me", color: undefined },
            { id: -3, type: "all", label: "All tasks", color: undefined },
            { id: -4, type: "add-list", label: "Add List", color: undefined },
        ];
        if (debouncedSearchQuery.length > 0) {
            flatItems = data ?? [];
        }
        if (!flatItems) return [];

        const groupedCommands: Record<string, SearchResult[]> = {};

        flatItems.forEach((item) => {
            const groupHeading = commandGroupHeadings[item.type];
            if (!groupedCommands[groupHeading]) {
                groupedCommands[groupHeading] = [];
            }
            groupedCommands[groupHeading].push(item);
        });

        const result = Object.entries(groupedCommands).map(([heading, items]) => ({
            heading,
            items: items.map(({ id, label, type, color }) => ({
                id,
                label,
                type,
                color,
            })),
        }));

        return result;
    }, [data, debouncedSearchQuery]);

    useEffect(() => {
        if (items.length > 0) {
            setSelectedValue(`${items[0].items[0].type}_${items[0].items[0].id}`);
        }
    }, [items]);

    const handleSelect = (command: { type: string; id: number }) => {
        setOpen(false);
        console.log("command", command);

        // For some reason using the typed version of the navigate (inboxRoute.to ex. ) doesn't work.
        switch (command.type) {
            case "inbox":
                console.log("inbox");
                navigate({ to: "/inbox", search: {} });
                break;
            case "assigned-to-me":
                navigate({ to: "/assigned-to-me", search: {} });
                break;
            case "all":
                navigate({ to: "/all", search: {} });
                break;
            case "add-list":
                setNewListDialogOpen(true);
                break;
            case "add-task":
                setNewTaskDialogOpen(true);
                break;
            case "task":
                navigate({ to: ".", search: { task: command.id } });
                break;
            case "tasklist":
                navigate({ to: "/lists/$taskListId", params: { taskListId: command.id }, search: {} });
                break;
        }
    };

    return (
        <CommandDialog open={open} onOpenChange={setOpen}>
            <Command shouldFilter={false} value={selectedValue} onValueChange={setSelectedValue}>
                <CustomCommandInput
                    isLoading={isFetching}
                    value={searchQuery}
                    onValueChange={(s) => setSearchQuery(s)}
                    placeholder="Type a command or search..."
                />
                <CommandList
                    className="relative"
                    defaultValue={`${items?.[0]?.items?.[0]?.type}_${items?.[0]?.items?.[0]?.id}`}
                >
                    {!isFetching && <CommandEmpty className="p-4">No results found.</CommandEmpty>}
                    {!isFetching &&
                        items.map((group) => (
                            <CommandGroup key={group.heading} heading={group.heading}>
                                {group.items.map((item) => (
                                    <CommandItem
                                        key={`${item.type}_${item.id}`}
                                        value={`${item.type}_${item.id}`}
                                        onSelect={() => {
                                            handleSelect(item);
                                        }}
                                    >
                                        {item.type === "tasklist" && (
                                            <TaskListBadgeColorShape color={item.color as TaskListColor} />
                                        )}
                                        {commandIcons[item.type]}
                                        {item.label}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        ))}
                </CommandList>
            </Command>
        </CommandDialog>
    );
}
